import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress,
  Snackbar,
  IconButton,
  Tooltip,
  FormHelperText
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Percent as PercentIcon,
  Factory as FactoryIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import api from '../services/api';
import { toast } from 'react-hot-toast';

const ProductionSettings = () => {
  const [settings, setSettings] = useState({
    oil_percentage: 20.00,
    cake_percentage: 72.00,
    sludge_percentage: 8.00
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/production/settings/current/');
      if (response.data) {
        setSettings({
          oil_percentage: parseFloat(response.data.oil_percentage) || 20.00,
          cake_percentage: parseFloat(response.data.cake_percentage) || 72.00,
          sludge_percentage: parseFloat(response.data.sludge_percentage) || 8.00
        });
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
      // Use defaults if error
      setSettings({
        oil_percentage: 20.00,
        cake_percentage: 72.00,
        sludge_percentage: 8.00
      });
      toast.info('Using default production settings (Oil: 20%, Cake: 72%, Sludge: 8%)');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    const value = parseFloat(event.target.value) || 0;
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Validate total percentage
      const total = settings.oil_percentage + settings.cake_percentage + settings.sludge_percentage;
      if (Math.abs(total - 100) > 0.01) {
        setError(`Total percentage must equal 100%. Current total: ${total.toFixed(2)}%`);
        setSaving(false);
        return;
      }

      const response = await api.post('/production/settings/', {
        oil_percentage: settings.oil_percentage,
        cake_percentage: settings.cake_percentage,
        sludge_percentage: settings.sludge_percentage
      });
      
      if (response.data) {
        setSuccess(true);
        toast.success('Production settings updated successfully!');
        await fetchSettings();
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.response?.data?.message || 'Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({
      oil_percentage: 20.00,
      cake_percentage: 72.00,
      sludge_percentage: 8.00
    });
    setError(null);
  };

  const handleCloseSnackbar = () => {
    setSuccess(false);
  };

  const totalPercentage = settings.oil_percentage + settings.cake_percentage + settings.sludge_percentage;
  const isTotalValid = Math.abs(totalPercentage - 100) <= 0.01;

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Production Settings
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Configure standard extraction percentages for production batches
            </Typography>
          </Box>
          <Box>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchSettings} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>How it works:</strong> When logging a production batch, entering the <strong>Shell Input (kg)</strong> 
            will automatically calculate the <strong>Oil Output</strong>, <strong>Cake Output</strong>, and 
            <strong>Sludge Output</strong> based on these percentages.
          </Typography>
        </Alert>

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <PercentIcon sx={{ color: '#2E7D32', mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">
                Extraction Percentages
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: '#FFD700',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}
                  >
                    O
                  </Box>
                  <TextField
                    fullWidth
                    label="Oil Extraction (%)"
                    type="number"
                    value={settings.oil_percentage}
                    onChange={handleChange('oil_percentage')}
                    InputProps={{ inputProps: { step: 0.01, min: 0, max: 100 } }}
                    helperText="Standard oil extraction percentage"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: '#8B7355',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: '#fff'
                    }}
                  >
                    C
                  </Box>
                  <TextField
                    fullWidth
                    label="Cake Extraction (%)"
                    type="number"
                    value={settings.cake_percentage}
                    onChange={handleChange('cake_percentage')}
                    InputProps={{ inputProps: { step: 0.01, min: 0, max: 100 } }}
                    helperText="Standard cake extraction percentage"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: '#808080',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: '#fff'
                    }}
                  >
                    S
                  </Box>
                  <TextField
                    fullWidth
                    label="Sludge Extraction (%)"
                    type="number"
                    value={settings.sludge_percentage}
                    onChange={handleChange('sludge_percentage')}
                    InputProps={{ inputProps: { step: 0.01, min: 0, max: 100 } }}
                    helperText="Standard sludge extraction percentage"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: '#1976D2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: '#fff'
                    }}
                  >
                    T
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      Total Percentage
                    </Typography>
                    <Typography 
                      variant="h5" 
                      fontWeight="bold" 
                      color={isTotalValid ? 'success.main' : 'error.main'}
                    >
                      {totalPercentage.toFixed(2)}%
                      {!isTotalValid && (
                        <Typography component="span" variant="caption" color="error" sx={{ ml: 1 }}>
                          (Must equal 100%)
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Preview Section */}
        <Card variant="outlined" sx={{ mb: 3, bgcolor: '#f5f5f5' }}>
          <CardContent>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Preview Calculation
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              For every <strong>1000 kg</strong> of shell input:
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#FFD700', borderRadius: 1 }}>
                  <Typography variant="caption" display="block">
                    Oil Output
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {(1000 * settings.oil_percentage / 100).toFixed(2)} kg
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {settings.oil_percentage}%
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#8B7355', borderRadius: 1, color: '#fff' }}>
                  <Typography variant="caption" display="block">
                    Cake Output
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {(1000 * settings.cake_percentage / 100).toFixed(2)} kg
                  </Typography>
                  <Typography variant="caption" color="#ddd">
                    {settings.cake_percentage}%
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#808080', borderRadius: 1, color: '#fff' }}>
                  <Typography variant="caption" display="block">
                    Sludge Output
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {(1000 * settings.sludge_percentage / 100).toFixed(2)} kg
                  </Typography>
                  <Typography variant="caption" color="#ddd">
                    {settings.sludge_percentage}%
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving || !isTotalValid}
            sx={{ minWidth: 120 }}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={saving}
          >
            Reset to Defaults
          </Button>
        </Box>

        {isTotalValid && settings.oil_percentage === 20 && settings.cake_percentage === 72 && settings.sludge_percentage === 8 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="textSecondary">
              <InfoIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} />
              Using standard default values (20% Oil, 72% Cake, 8% Sludge)
            </Typography>
          </Box>
        )}
      </Paper>

      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success">
          Production settings saved successfully!
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProductionSettings;